'use client';
import { SportStrip } from '@/components/molecules/Sports/SportStrip';
import type { Details as Team } from '@/components/molecules/Sports/SportStrip';
import dayjs from 'dayjs';
import timeZoneExtension from 'dayjs/plugin/timezone';
import isBetween from 'dayjs/plugin/isBetween';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import styles from './sportschedule.module.scss';
import { useMemo, useState } from 'react';
import { Event } from '@/packages/sxm/sports/events/EventsResponse';
import { DateOption, DateRange, GameList, EventProps } from './interfaces';
import filters from './league.config';
import { DynamicTableRow } from '@/components/molecules/DynamicTable/DynamicTable';
import SXMLink from '@/shared-components/SXMLink';
import NoGames from './NoGames';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { StyledContainer } from '@/ui-components/atoms/Layout/StyledContainer/StyledContainer';
import { Text } from '@/ui-components/atoms/Text/Text';
import { Option } from '@/ui-components/atoms/Inputs/Select/Option';
import { OptionGroup } from '@/ui-components/atoms/Inputs/Select/OptionGroup';
import { Select } from '@/ui-components/atoms/Inputs/Select/Select';
import { formatSatelliteChannel, renderChannelLink } from './channelCells';
import { buildListenOptions, resolveChannel, toChannelLabel } from '@/packages/sxm/sports/channelWaterfall';

export default function LeagueSchedule({
    leagueAbbr,
    remind_me_logo,
    remind_me_title,
    reminder_url,
    events,
    playerUrl,
}: EventProps) {
    dayjs.extend(advancedFormat);
    dayjs.extend(timeZoneExtension);
    dayjs.extend(isBetween);

    const today = dayjs();

    const dateOptions = useMemo<DateOption[]>(() => {
        const ranges: string[] = [];
        const dateOptions: DateOption[] = [];

        events?.forEach((event: Event) => {
            if (event.eventName) {
                const filterData = filters[leagueAbbr] || filters.default;

                const eventDate = dayjs(event.gameDate);
                const endedAt = event.gameEndDate ? dayjs(event.gameEndDate) : eventDate.endOf('day');
                if (today.isAfter(endedAt)) {
                    return;
                }

                let start = eventDate.startOf('day');
                let end = eventDate.endOf('day');
                let label = start.isSame(today, 'day') ? start.format('[Today], MMMM D') : start.format('dddd, MMMM D');

                if (filterData.schedule === 'monthly') {
                    start = eventDate.startOf('month');
                    end = eventDate.endOf('month');
                    label = start.format('MMMM');
                } else if (filterData.schedule === 'weekly') {
                    if (filterData.weekStart) {
                        if (start.day() < filterData.weekStart) {
                            start = start.day(-(filterData.weekStart - 1));
                        } else if (start.day() > filterData.weekStart) {
                            start = start.startOf('week');
                            start = start.day(filterData.weekStart);
                        }
                    }

                    start = start.startOf('day');
                    end = start.clone().add(6, 'day').endOf('day');
                    const endLabel = start.isSame(end, 'month') ? end.format('D') : end.format('MMM D');
                    label = start.format(`MMM D [- ${endLabel}]`);
                }

                const rangeIndex = `${start}${end}`;

                if (ranges.indexOf(rangeIndex) === -1) {
                    ranges.push(rangeIndex);

                    dateOptions.push({
                        range: {
                            start: start,
                            end: end,
                        },
                        value: label,
                        label: label,
                    });
                }
            }
        });

        return dateOptions;
    }, [events, today, leagueAbbr]);

    const [dateRange, setDateRange] = useState<DateRange>(
        dateOptions[0] ? dateOptions[0].range : { start: dayjs(), end: dayjs() },
    );

    const [eventDates, dates] = useMemo<[GameList, string[]]>(() => {
        const eventDates: GameList = {};
        const dates: string[] = [];

        if (events) {
            for (let i = 0; i < events.length; i += 1) {
                const event = events[i] as Event;
                const index = event.gameDate || '';
                const eventDate = dayjs(event.gameDate);

                const endedAt = event.gameEndDate ? dayjs(event.gameEndDate) : eventDate.endOf('day');
                if (today.isAfter(endedAt) || !eventDate.isBetween(dateRange.start, dateRange.end)) {
                    continue;
                }

                if (!eventDates[index]) {
                    const dateStr = eventDate.format('dddd, MMMM D • h:mm A z');
                    eventDates[index] = [
                        <Text
                            key={`header-${index}`}
                            variant="text-small"
                            tag="h3"
                            className={styles.dateTitle}
                            data-cy="components-contextual-sports-date-title"
                        >
                            {dateStr}
                        </Text>,
                    ];
                    dates.push(index);
                }

                const leagueEvent: Team = {
                    logo: event.homeLogo,
                    pageUrl: event.homeUrl,
                    playerUrl: playerUrl,
                    name: event.eventName || '',
                    location: event.venueName || '',
                    channel: toChannelLabel(resolveChannel(event, 'national')),
                };

                const moreWays: DynamicTableRow[] = buildListenOptions(event).map((option) => ({
                    rowLabel: option.label,
                    items: [
                        formatSatelliteChannel(option.channel),
                        renderChannelLink(
                            option.deepLink,
                            option.internetChannel,
                            `Listen to ${event.eventName} on ${option.internetChannel}`,
                        ),
                    ],
                }));

                const isLive = dayjs().isBetween(event.gameDate, event.gameEndDate);
                eventDates[index].push(
                    <li key={`event-${event.gameDate}-${event.eventName}-${event.venueName ?? ''}`}>
                        <SportStrip
                            league={leagueEvent}
                            moreWays={moreWays}
                            isLive={isLive}
                            ariaLabel={`Listen to ${event.eventName} on ${event.nationalInternetStream}`}
                        />
                    </li>,
                );
            }
        }

        return [eventDates, dates];
    }, [events, dateRange, playerUrl, today]);

    function dateSelectHandler(element: HTMLInputElement, { data }: { data?: DateRange }) {
        if (data) {
            setDateRange(data);
        }
    }

    if (dates.length > 0) {
        return (
            <Grid container className={styles.scheduleContainer}>
                <Grid col={{ sm: 12, lg: 10 }} start={{ lg: 2 }}>
                    <div className={styles.filterBar}>
                        <Select<DateRange>
                            label="filter events by date"
                            type="bar"
                            value={dateOptions[0].value}
                            name="dateFilter"
                            onChange={dateSelectHandler}
                            className={styles.filterBarSelect}
                        >
                            <OptionGroup>
                                {dateOptions.map((option, i) => (
                                    <Option key={i} object={option.range} value={option.value}>
                                        {option.label}
                                    </Option>
                                ))}
                                <Option value="" disableOption className={styles.moreGames}>
                                    More events will be added as information becomes available.
                                </Option>
                            </OptionGroup>
                        </Select>
                    </div>

                    {dates.sort().map((index) => (
                        <ul key={index} className={styles.listStylings}>
                            {eventDates[index]}
                        </ul>
                    ))}

                    <StyledContainer paddingTop={48}>
                        <NoGames
                            logo={remind_me_logo}
                            logoSize="small"
                            title={remind_me_title || 'Never Miss a Moment'}
                        >
                            <SXMLink href={reminder_url} dataCy="contentful-event-schedule-add-calendar-link">
                                Add to calendar
                            </SXMLink>
                        </NoGames>
                    </StyledContainer>
                </Grid>
            </Grid>
        );
    }

    return <></>;
}
